import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";
import { api, logout, isLoggedIn, API } from "../../lib/api";
import { TID, LOGO_URL } from "../../lib/brand";
import HeroSlidesTab from "./HeroSlidesTab";
import GalleryCMS from "./GalleryCMS";
import AuthorizationPanel from "./AuthorizationPanel";
import CrmTab from "./CrmTab";

const STATUSES = [
  "new", "contacted", "needs_info", "inspection_scheduled",
  "estimate_in_progress", "estimate_sent", "authorization_sent",
  "customer_authorized", "approved", "scheduled",
  "in_repair", "ready", "completed", "declined", "archived",
];

const PTYPE_LABEL = {
  collision_insurance: "COLLISION / INSURANCE",
  custom_jdm_specialty: "CUSTOM / JDM / SPECIALTY",
  unsure: "NEEDS CLASSIFICATION",
};
const PTYPE_TONE = {
  collision_insurance: "border-proform-red text-proform-red",
  custom_jdm_specialty: "border-chrome-100 text-chrome-100",
  unsure: "border-chrome-300 text-chrome-300",
};

function LeadBadge({ value }) {
  const label = PTYPE_LABEL[value] || "UNCATEGORIZED";
  const tone = PTYPE_TONE[value] || "border-chrome-300 text-chrome-300";
  return (
    <span className={`inline-flex items-center gap-1.5 border px-2 py-1 font-mono text-[10px] tracking-[0.2em] ${tone}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" /> {label}
    </span>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("estimates");
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pfilter, setPfilter] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [counts, setCounts] = useState({ active: 0, archived: 0, total: 0 });

  useEffect(() => {
    if (!isLoggedIn()) { nav("/admin/login"); return; }
    load();
    // load is stable enough; re-run when the "show archived" toggle changes
  }, [includeArchived]);

  const load = async () => {
    setLoading(true);
    try {
      const [e, p, pr, c] = await Promise.all([
        api.get(`/admin/estimates?include_archived=${includeArchived}`),
        api.get("/admin/projects").catch(() => ({ data: [] })),
        api.get("/admin/partners").catch(() => ({ data: [] })),
        api.get("/admin/estimates/counts").catch(() => ({ data: { active: 0, archived: 0, total: 0 } })),
      ]);
      setEstimates(e.data || []);
      setProjects(p.data || []);
      setPartners(pr.data || []);
      setCounts(c.data || { active: 0, archived: 0, total: 0 });
      // Keep selection consistent after reload
      setSelected((s) => (s ? (e.data || []).find((x) => x.reference === s.reference) || null : null));
    } catch (e) {
      if (e?.response?.status === 401) { logout(); nav("/admin/login"); }
    } finally { setLoading(false); }
  };

  const filteredEstimates = pfilter
    ? estimates.filter((e) => (e.project_type || "") === pfilter)
    : estimates;

  const updateStatus = async (reference, status, note) => {
    await api.patch(`/admin/estimates/${reference}`, { status, note });
    await load();
  };

  const updateProjectType = async (reference, project_type) => {
    await api.patch(`/admin/estimates/${reference}`, { project_type });
    await load();
    setSelected((s) => s && s.reference === reference ? { ...s, project_type } : s);
  };

  const archiveEstimate = async (reference) => {
    if (!window.confirm("Archive this estimate? It will be hidden from the default view but kept in the database.")) return;
    await api.patch(`/admin/estimates/${reference}`, { status: "archived" });
    setSelected(null);
    await load();
  };

  const deleteEstimate = async (reference) => {
    if (!window.confirm(`Permanently delete ${reference}? This removes the record AND any uploaded customer photos and CANNOT be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? Type of confirm to continue.`)) return;
    await api.delete(`/admin/estimates/${reference}`);
    setSelected(null);
    await load();
  };

  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <>
      <SEO title="ProForm Admin" description="ProForm admin dashboard." />
      <div data-testid={TID.admin.dashboard} className="min-h-screen">
        {/* Header */}
        <header className="border-b border-ink-700 sticky top-0 z-30 bg-ink-950/90 backdrop-blur">
          <div className="wrap flex items-center justify-between h-16">
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ProForm" className="h-8" />
              <span className="label">Admin</span>
            </Link>
            <nav className="flex items-center gap-6">
              {[
                ["estimates", "Estimates"],
                ["crm", "CRM"],
                ["hero", "Hero"],
                ["projects", "Gallery"],
                ["partners", "Partners"],
              ].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                        className={`font-mono text-[11px] tracking-[0.24em] uppercase ${tab === k ? "text-proform-red" : "text-chrome-300 hover:text-chrome-100"}`}>
                  {l}
                </button>
              ))}
              <button data-testid={TID.admin.logout} onClick={doLogout} className="btn">Logout</button>
            </nav>
          </div>
        </header>

        <div className="wrap py-10">
          {loading ? <div className="label">Loading…</div> : null}

          {tab === "estimates" && (
            <div className="grid lg:grid-cols-[1fr,420px] gap-8">
              <div>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                  <div>
                    <h1 className="font-display uppercase text-3xl">Estimate Requests</h1>
                    <div className="label mt-1">{counts.active} active · {counts.archived} archived · {counts.total} total</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="label flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={includeArchived}
                        onChange={(e) => setIncludeArchived(e.target.checked)}
                        className="accent-proform-red"
                        data-testid="admin-include-archived"
                      />
                      Show archived
                    </label>
                    <label className="label">Filter:</label>
                    <select
                      value={pfilter}
                      onChange={(e) => setPfilter(e.target.value)}
                      className="field py-2"
                      data-testid="admin-ptype-filter"
                    >
                      <option value="">All categories</option>
                      <option value="collision_insurance">Collision / Insurance</option>
                      <option value="custom_jdm_specialty">Custom / JDM / Specialty</option>
                      <option value="unsure">Needs Classification</option>
                    </select>
                    <div className="label">{filteredEstimates.length} shown</div>
                  </div>
                </div>
                <div className="border border-ink-700 overflow-x-auto">
                  <table data-testid={TID.admin.estimatesTable} className="w-full text-sm">
                    <thead className="bg-ink-900">
                      <tr className="text-left label border-b border-ink-700">
                        <th className="p-3">Ref</th>
                        <th className="p-3">Category</th>
                        <th className="p-3">Customer</th>
                        <th className="p-3">Vehicle</th>
                        <th className="p-3">Photos</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Received</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEstimates.map((e) => (
                        <tr key={e.reference}
                            onClick={() => setSelected(e)}
                            className={`border-b border-ink-700 cursor-pointer hover:bg-ink-900 ${selected?.reference === e.reference ? "bg-ink-800" : ""}`}>
                          <td className="p-3 font-mono text-proform-red whitespace-nowrap">{e.reference}</td>
                          <td className="p-3"><LeadBadge value={e.project_type} /></td>
                          <td className="p-3">{e.first_name} {e.last_name}<div className="text-chrome-300 text-xs">{e.phone}</div></td>
                          <td className="p-3 text-chrome-300 whitespace-nowrap">{e.vehicle_year} {e.vehicle_make} {e.vehicle_model}</td>
                          <td className="p-3 text-chrome-300">{e.files?.length || 0}</td>
                          <td className="p-3"><span className="label">{e.status}</span></td>
                          <td className="p-3 text-chrome-300 whitespace-nowrap">{e.created_at?.slice(0, 10)}</td>
                        </tr>
                      ))}
                      {filteredEstimates.length === 0 && (
                        <tr><td colSpan="7" className="p-8 text-center text-chrome-300">No estimates in this category yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <aside className="border border-ink-700 p-6 h-fit sticky top-24">
                {selected ? (
                  <>
                    <div className="mb-3"><LeadBadge value={selected.project_type} /></div>
                    <div className="label mb-2">Reference</div>
                    <div className="font-display text-3xl text-proform-red">{selected.reference}</div>
                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div><div className="label">Name</div>{selected.first_name} {selected.last_name}</div>
                      <div><div className="label">Contact</div>{selected.preferred_contact}</div>
                      <div><div className="label">Phone</div><a href={`tel:${selected.phone}`} className="hover:text-proform-red">{selected.phone}</a></div>
                      <div><div className="label">Email</div><a href={`mailto:${selected.email}`} className="hover:text-proform-red break-all">{selected.email}</a></div>
                      <div className="col-span-2"><div className="label">Vehicle</div>{selected.vehicle_year} {selected.vehicle_make} {selected.vehicle_model}</div>
                      <div className="col-span-2"><div className="label">Work type</div>{selected.work_type}</div>
                      <div className="col-span-2"><div className="label">Description</div><div className="text-chrome-100 whitespace-pre-line">{selected.description}</div></div>
                      {selected.insurance_company && <div className="col-span-2"><div className="label">Insurance</div>{selected.insurance_company} — claim {selected.claim_number || "—"}</div>}
                    </div>

                    {selected.files?.length > 0 && (
                      <div className="mt-6">
                        <div className="label mb-3">Damage Photos ({selected.files.length})</div>
                        <div className="grid grid-cols-3 gap-2">
                          {selected.files.map((f, i) => (
                            <a key={i} href={`${API.replace('/api','')}${f.url}`} target="_blank" rel="noreferrer" className="clip-frame aspect-square block">
                              <img src={`${API.replace('/api','')}${f.url}`} alt={f.original} />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.insurance_files?.length > 0 && (
                      <div className="mt-6" data-testid="admin-insurance-files">
                        <div className="label mb-3">Insurance Documents ({selected.insurance_files.length})</div>
                        <ul className="space-y-2">
                          {selected.insurance_files.map((f, i) => (
                            <li key={i} className="flex items-center gap-3 border border-ink-700 p-2 text-sm">
                              <div className="flex-1 min-w-0">
                                <div className="text-chrome-100 truncate">{f.original}</div>
                                <div className="label">{f.category?.replace(/_/g, ' ')} · {(f.size/1024).toFixed(0)} KB</div>
                              </div>
                              <a href={`${API.replace('/api','')}${f.url}`} target="_blank" rel="noreferrer" className="label hover:text-proform-red">Open ↗</a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(selected.adjuster_first_name || selected.adjuster_last_name || selected.adjuster_email || selected.adjuster_phone) && (
                      <div className="mt-6" data-testid="admin-adjuster-block">
                        <div className="label mb-2">Adjuster</div>
                        <div className="text-sm text-chrome-100 space-y-1">
                          {(selected.adjuster_first_name || selected.adjuster_last_name) && (
                            <div>{selected.adjuster_first_name} {selected.adjuster_last_name}{selected.adjuster_company ? ` · ${selected.adjuster_company}` : ""}</div>
                          )}
                          {selected.adjuster_phone && <div><a href={`tel:${selected.adjuster_phone}`} className="hover:text-proform-red">{selected.adjuster_phone}</a>{selected.adjuster_extension && <span className="text-chrome-300"> · ext. {selected.adjuster_extension}</span>}</div>}
                          {selected.adjuster_email && <div><a href={`mailto:${selected.adjuster_email}`} className="hover:text-proform-red break-all">{selected.adjuster_email}</a></div>}
                          {selected.adjuster_notes && <div className="text-chrome-300 text-xs italic">{selected.adjuster_notes}</div>}
                        </div>
                      </div>
                    )}

                    {selected.acknowledgment && (
                      <div className="mt-6" data-testid="admin-ack-block">
                        <div className="label mb-2">Preliminary Review Acknowledgment · Audit</div>
                        <div className="border border-ink-700 p-3 text-sm space-y-1">
                          <div><span className="label">Signed by</span><div className="text-chrome-100">{selected.acknowledgment.legal_name}</div></div>
                          <div><span className="label">Version</span><div className="text-chrome-100 font-mono text-xs">{selected.acknowledgment.version}</div></div>
                          <div><span className="label">Timestamp (UTC)</span><div className="text-chrome-100 font-mono text-xs">{selected.acknowledgment.timestamp_utc}</div></div>
                          <div><span className="label">IP · UA</span><div className="text-chrome-100 font-mono text-[10px] break-all">{selected.acknowledgment.ip_address} · {selected.acknowledgment.user_agent}</div></div>
                          {selected.acknowledgment.signature_url && (
                            <div>
                              <span className="label">Drawn signature</span>
                              <img src={`${API.replace('/api','')}${selected.acknowledgment.signature_url}`} alt="Customer signature" className="mt-1 h-16 bg-ink-950 border border-ink-700" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-6">
                      <div className="label mb-2">Update status</div>
                      <select
                        className="field"
                        value={selected.status}
                        onChange={(e) => updateStatus(selected.reference, e.target.value)}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>

                    <div className="mt-4">
                      <div className="label mb-2">Reclassify category</div>
                      <select
                        className="field"
                        value={selected.project_type || ""}
                        onChange={(e) => updateProjectType(selected.reference, e.target.value)}
                        data-testid="admin-reclassify"
                      >
                        <option value="unsure">Needs Classification</option>
                        <option value="collision_insurance">Collision / Insurance</option>
                        <option value="custom_jdm_specialty">Custom / JDM / Specialty</option>
                      </select>
                    </div>

                    <div className="mt-4">
                      <NoteForm reference={selected.reference} onSaved={load} />
                    </div>

                    {selected.notes?.length > 0 && (
                      <div className="mt-6">
                        <div className="label mb-2">Notes</div>
                        <ul className="space-y-3">
                          {selected.notes.map((n, i) => (
                            <li key={i} className="border-l-2 border-proform-red pl-3">
                              <div className="label">{n.at?.slice(0, 16).replace('T',' ')} · {n.by}</div>
                              <div>{n.text}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-8 pt-6 border-t border-ink-700">
                      <div className="label mb-3">Danger zone</div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          type="button"
                          onClick={() => archiveEstimate(selected.reference)}
                          data-testid="admin-archive-estimate"
                          className="btn flex-1 justify-center"
                          disabled={selected.status === "archived"}
                        >
                          {selected.status === "archived" ? "Archived" : "Archive"}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteEstimate(selected.reference)}
                          data-testid="admin-delete-estimate"
                          className="btn flex-1 justify-center border-proform-red text-proform-red hover:bg-proform-red hover:text-chrome-100"
                        >
                          Delete permanently
                        </button>
                      </div>
                      <p className="text-chrome-300 text-xs mt-2">
                        Archive hides from the default list (reversible). Delete removes the record and any uploaded customer photos (irreversible).
                      </p>
                    </div>

                    <AuthorizationPanel source={selected} sourceType="estimate" onSent={load} onSourceUpdated={load} />
                  </>
                ) : (
                  <div className="text-chrome-300 text-sm">Select an inquiry to view details, update status, and add notes.</div>
                )}
              </aside>
            </div>
          )}

          {tab === "projects" && <GalleryCMS projects={projects} onChange={load} />}
          {tab === "partners" && <PartnersCMS partners={partners} onChange={load} />}
          {tab === "hero" && <HeroSlidesTab />}
          {tab === "crm" && <CrmTab />}
        </div>
      </div>
    </>
  );
}

function NoteForm({ reference, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    await api.patch(`/admin/estimates/${reference}`, { status: "contacted", note: text });
    setText(""); setBusy(false); onSaved && onSaved();
  };
  return (
    <div>
      <div className="label mb-2">Add note</div>
      <textarea className="field" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
      <button className="btn mt-2" onClick={submit} disabled={busy || !text.trim()}>{busy ? "Saving…" : "Save note"}</button>
    </div>
  );
}

function PartnersCMS({ partners, onChange }) {
  const [form, setForm] = useState({ name: "", website: "", instagram: "", description: "", relationship_type: "referral", permission_confirmed: false, featured: false, sort_order: 0 });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/partners", form); onChange && onChange(); setForm((f) => ({ ...f, name: "", website: "" })); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete partner?")) return;
    await api.delete(`/admin/partners/${id}`); onChange && onChange();
  };
  return (
    <div className="grid lg:grid-cols-[1fr,420px] gap-8">
      <div>
        <h1 className="font-display uppercase text-3xl mb-6">Partners</h1>
        <div className="grid md:grid-cols-2 gap-4">
          {partners.map((p) => (
            <div key={p.id} className="card p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="label">{p.relationship_type || "partner"}</div>
                  <div className="font-display text-2xl mt-1">{p.name}</div>
                  <div className="text-chrome-300 text-sm mt-1">{p.website}</div>
                </div>
                <button onClick={() => del(p.id)} className="label hover:text-proform-red">Delete</button>
              </div>
            </div>
          ))}
          {partners.length === 0 && <div className="text-chrome-300 text-sm">No partners yet.</div>}
        </div>
      </div>
      <aside className="card p-6 h-fit sticky top-24">
        <div className="label mb-3">Add partner</div>
        <form onSubmit={submit} className="space-y-3">
          <input required className="field" placeholder="Name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <input className="field" placeholder="Website" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
          <input className="field" placeholder="Instagram" value={form.instagram} onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))} />
          <textarea className="field" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <label className="flex items-center gap-2 text-sm text-chrome-300">
            <input type="checkbox" checked={form.permission_confirmed} onChange={(e) => setForm((f) => ({ ...f, permission_confirmed: e.target.checked }))} /> Logo permission confirmed
          </label>
          <button className="btn btn-primary w-full justify-center" disabled={busy || !form.name}>{busy ? "Saving…" : "Add partner"}</button>
        </form>
      </aside>
    </div>
  );
}
